import React, { useState, useEffect, useCallback } from 'react';
import { acquistiApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import {
  Loader2, Plus, Pencil, Trash2, AlertTriangle, CalendarClock, Repeat, Check, EyeOff,
} from 'lucide-react';
import AcquistiHeader from '../../components/acquisti/AcquistiHeader';
import CostoFissoForm from '../../components/acquisti/CostoFissoForm';
import PeriodPicker from '../../components/venduto/PeriodPicker';
import { periodoRange, defaultSelezione } from '../../lib/venduto';
import { fmtSoldi, fmtDataIt } from '../../lib/acquisti';

const MESI_BREVI = ['', 'gen', 'feb', 'mar', 'apr', 'mag', 'giu', 'lug', 'ago', 'set', 'ott', 'nov', 'dic'];
const fmtMese = (m) => (m ? `${MESI_BREVI[Number(m.slice(5, 7))]} ${m.slice(0, 4)}` : '');

const AcquistiCostiFissiPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.ruolo === 'admin' || user?.is_super_admin;
  const [stato, setStato] = useState(null);
  const [sel, setSel] = useState(null);
  const [dati, setDati] = useState(null);
  const [caricando, setCaricando] = useState(true);
  const [formAperto, setFormAperto] = useState(false);
  const [inModifica, setInModifica] = useState(null);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    acquistiApi.getStato().then((s) => {
      setStato(s);
      setSel(defaultSelezione(s));
    }).catch(() => setCaricando(false));
  }, []);

  const carica = useCallback(async (r) => {
    setCaricando(true);
    try {
      setDati(await acquistiApi.getCostiFissi(r.data_inizio, r.data_fine));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nel caricamento dei costi fissi');
    } finally {
      setCaricando(false);
    }
  }, []);

  const range = sel ? periodoRange(sel, stato) : null;
  useEffect(() => {
    if (range) carica(range);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range?.data_inizio, range?.data_fine]);

  const salva = async (payload) => {
    setSalvando(true);
    try {
      if (inModifica) {
        await acquistiApi.aggiornaCostoFisso(inModifica.id, payload);
        toast.success('Costo fisso aggiornato');
      } else {
        await acquistiApi.creaCostoFisso(payload);
        toast.success('Costo fisso salvato: le rate entrano subito nei costi totali');
      }
      setFormAperto(false);
      setInModifica(null);
      if (range) carica(range);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Salvataggio non riuscito');
    } finally {
      setSalvando(false);
    }
  };

  const elimina = async (costo) => {
    if (!window.confirm(`Eliminare «${costo.nome}» e tutte le sue rate dai costi?`)) return;
    try {
      await acquistiApi.eliminaCostoFisso(costo.id);
      toast.success('Costo fisso eliminato');
      if (range) carica(range);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Eliminazione non riuscita');
    }
  };

  const azioneMese = async (fn, id, mese, valore, messaggio) => {
    try {
      await fn(id, mese, valore);
      toast.success(messaggio);
      if (range) carica(range);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Operazione non riuscita');
    }
  };

  const p = dati?.periodo;
  const doppioni = p?.doppioni || [];

  return (
    <div className="min-h-screen pb-24">
      <AcquistiHeader
        titolo="Costi fissi ricorrenti"
        sottotitolo={range?.label || 'Periodo'}
        azione={isAdmin && (
          <Button
            size="sm"
            className="shrink-0 h-9 text-xs"
            onClick={() => { setInModifica(null); setFormAperto(true); }}
            data-testid="costi-fissi-nuovo"
          >
            <Plus className="w-4 h-4 mr-1" /> Nuovo
          </Button>
        )}
      />

      <main className="px-4 py-4 max-w-lg mx-auto space-y-4">
        {sel && <PeriodPicker value={sel} onChange={setSel} archivio={stato} testid="costi-fissi-periodo" />}

        {caricando && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" /> Caricamento…
          </div>
        )}

        {dati && (
          <>
            <Card data-testid="costi-fissi-kpi">
              <CardContent className="p-4 space-y-3">
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                    Rate in scadenza nel periodo
                  </p>
                  <p className="text-3xl font-bold tabular-nums" data-testid="costi-fissi-totale-periodo">
                    {fmtSoldi(p.totale)}
                  </p>
                  <p className="text-[11px] text-muted-foreground">
                    {p.n_rate} rat{p.n_rate === 1 ? 'a' : 'e'} di {p.n_costi} cost{p.n_costi === 1 ? 'o' : 'i'}
                    {' '}· già conteggiate nei costi totali e nel bilancio
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-3 pt-2 border-t">
                  <div>
                    <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Impegno mensile</p>
                    <p className="font-semibold tabular-nums" data-testid="costi-fissi-impegno-mensile">
                      {fmtSoldi(dati.impegno_mensile)}
                    </p>
                    <p className="text-[11px] text-muted-foreground">equivalente al mese, piani attivi</p>
                  </div>
                  <div>
                    <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Residuo da pagare</p>
                    <p className="font-semibold tabular-nums" data-testid="costi-fissi-residuo">
                      {fmtSoldi(dati.residuo_totale)}
                    </p>
                    <p className="text-[11px] text-muted-foreground">rate future dei piani con scadenza</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {doppioni.length > 0 && (
              <Card className="border-amber-300" data-testid="costi-fissi-doppioni">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2 text-amber-800">
                    <AlertTriangle className="w-4 h-4" /> {doppioni.length} possibili doppioni
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-xs text-muted-foreground">
                    Nello stesso mese risulta anche una fattura o un'uscita di cassa di importo simile
                    (±5%). La rata resta conteggiata finché non decidi tu.
                  </p>
                  {doppioni.map((d) => (
                    <div
                      key={`${d.costo_id}-${d.mese}`}
                      className="rounded-lg border border-amber-200 bg-amber-50/60 p-3 space-y-2"
                      data-testid={`doppione-${d.costo_id}-${d.mese}`}
                    >
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="text-sm font-semibold">{d.nome}</span>
                        <span className="text-sm font-bold tabular-nums">{fmtSoldi(d.importo)}</span>
                      </div>
                      <p className="text-[11px] text-muted-foreground">
                        Rata di {fmtMese(d.mese)} (scadenza {fmtDataIt(d.data)})
                      </p>
                      {d.candidati.map((c) => (
                        <p key={`${c.fonte}-${c.id}`} className="text-[11px] text-amber-800">
                          {c.fonte === 'fattura' ? 'Fattura' : 'Uscita di cassa'}: {c.riferimento} ·
                          {' '}{fmtSoldi(c.importo)} del {fmtDataIt(c.data)}
                        </p>
                      ))}
                      {isAdmin && (
                        <div className="flex flex-wrap gap-2 pt-1">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 text-xs"
                            onClick={() => azioneMese(
                              acquistiApi.ignoraDoppioneCostoFisso, d.costo_id, d.mese, true,
                              'Avviso silenziato: la rata resta nei costi',
                            )}
                            data-testid={`doppione-ignora-${d.costo_id}-${d.mese}`}
                          >
                            <Check className="w-3.5 h-3.5 mr-1" /> Non è un doppione
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 text-xs border-amber-400 text-amber-900"
                            onClick={() => azioneMese(
                              acquistiApi.escludiMeseCostoFisso, d.costo_id, d.mese, true,
                              'Rata esclusa da questo mese',
                            )}
                            data-testid={`doppione-escludi-${d.costo_id}-${d.mese}`}
                          >
                            <EyeOff className="w-3.5 h-3.5 mr-1" /> Escludi la rata
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {dati.costi.length === 0 ? (
              <Card data-testid="costi-fissi-vuoto">
                <CardContent className="p-4 space-y-2">
                  <p className="text-sm font-semibold">Nessun costo fisso registrato</p>
                  <p className="text-xs text-muted-foreground">
                    Qui vanno i costi ricorrenti senza fattura elettronica: affitto, rate di
                    finanziamento, leasing, assicurazioni, tributi. Indica importo, cadenza e
                    durata: le rate entrano automaticamente nei costi totali e nel bilancio
                    mensile.
                  </p>
                  {isAdmin && (
                    <Button
                      size="sm"
                      className="mt-1"
                      onClick={() => { setInModifica(null); setFormAperto(true); }}
                      data-testid="costi-fissi-nuovo-vuoto"
                    >
                      <Plus className="w-4 h-4 mr-1" /> Aggiungi il primo costo fisso
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              dati.costi.map((c) => {
                const r = c.riepilogo;
                const spento = c.attivo === false;
                const rataPeriodo = c.rate_periodo.filter((x) => !x.escluso).pop();
                return (
                  <Card
                    key={c.id}
                    className={spento ? 'opacity-60' : undefined}
                    data-testid={`costo-fisso-${c.id}`}
                  >
                    <CardContent className="p-4 space-y-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="font-semibold truncate">{c.nome}</p>
                          {c.beneficiario && (
                            <p className="text-[11px] text-muted-foreground truncate">{c.beneficiario}</p>
                          )}
                          <div className="flex flex-wrap items-center gap-1.5 mt-1">
                            <Badge variant="secondary" className="text-[10px]">{c.categoria}</Badge>
                            <Badge variant="outline" className="text-[10px]">
                              <Repeat className="w-3 h-3 mr-1" /> {r.cadenza_label}
                            </Badge>
                            {spento && <Badge variant="outline" className="text-[10px]">sospeso</Badge>}
                            {r.concluso && <Badge variant="outline" className="text-[10px]">concluso</Badge>}
                            {c.doppioni.length > 0 && (
                              <Badge className="text-[10px] bg-amber-100 text-amber-900 hover:bg-amber-100">
                                possibile doppione
                              </Badge>
                            )}
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-lg font-bold tabular-nums">{fmtSoldi(c.importo)}</p>
                          <p className="text-[11px] text-muted-foreground">per rata</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3 pt-2 border-t text-sm">
                        <div>
                          <p className="text-[11px] uppercase text-muted-foreground">Piano</p>
                          <p className="font-semibold">
                            {c.senza_fine
                              ? 'Senza fine'
                              : r.n_rate
                                ? `rata ${(rataPeriodo?.rata ?? r.rata_corrente) || 0} di ${r.n_rate}`
                                : '—'}
                          </p>
                          <p className="text-[11px] text-muted-foreground">
                            {!c.senza_fine && r.n_rate && (rataPeriodo ? 'rata del periodo · ' : 'a oggi · ')}
                            da {fmtMese(c.mese_inizio)}
                            {c.mese_fine ? ` a ${fmtMese(c.mese_fine)}` : ''}
                          </p>
                        </div>
                        <div>
                          <p className="text-[11px] uppercase text-muted-foreground">Prossima scadenza</p>
                          <p className="font-semibold flex items-center gap-1">
                            <CalendarClock className="w-3.5 h-3.5 text-muted-foreground" />
                            {r.prossima_scadenza ? fmtDataIt(r.prossima_scadenza) : 'nessuna'}
                          </p>
                          <p className="text-[11px] text-muted-foreground">
                            {c.metodo_pagamento}
                          </p>
                        </div>
                        <div>
                          <p className="text-[11px] uppercase text-muted-foreground">Nel periodo</p>
                          <p className="font-semibold tabular-nums" data-testid={`costo-fisso-periodo-${c.id}`}>
                            {fmtSoldi(c.totale_periodo)}
                          </p>
                          <p className="text-[11px] text-muted-foreground">
                            {c.rate_periodo.filter((x) => !x.escluso).length} rat
                            {c.rate_periodo.filter((x) => !x.escluso).length === 1 ? 'a' : 'e'}
                          </p>
                        </div>
                        <div>
                          <p className="text-[11px] uppercase text-muted-foreground">Residuo</p>
                          <p className="font-semibold tabular-nums">
                            {r.residuo === null || r.residuo === undefined ? '—' : fmtSoldi(r.residuo)}
                          </p>
                          <p className="text-[11px] text-muted-foreground">
                            {r.totale_piano ? `piano ${fmtSoldi(r.totale_piano)}` : 'ricorrenza aperta'}
                          </p>
                        </div>
                      </div>

                      {c.note && <p className="text-[11px] text-muted-foreground">{c.note}</p>}

                      {r.mesi_esclusi.length > 0 && (
                        <div className="flex flex-wrap items-center gap-1.5 pt-1">
                          <span className="text-[11px] text-muted-foreground">Mesi esclusi:</span>
                          {r.mesi_esclusi.map((m) => (
                            <button
                              key={m}
                              disabled={!isAdmin}
                              onClick={() => azioneMese(
                                acquistiApi.escludiMeseCostoFisso, c.id, m, false,
                                `Rata di ${fmtMese(m)} rimessa nei costi`,
                              )}
                              className="text-[11px] rounded-full border px-2 py-0.5 hover:bg-secondary"
                              data-testid={`costo-fisso-escluso-${c.id}-${m}`}
                            >
                              {fmtMese(m)} ✕
                            </button>
                          ))}
                        </div>
                      )}

                      {isAdmin && (
                        <div className="flex gap-2 pt-1">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 text-xs"
                            onClick={() => { setInModifica(c); setFormAperto(true); }}
                            data-testid={`costo-fisso-modifica-${c.id}`}
                          >
                            <Pencil className="w-3.5 h-3.5 mr-1" /> Modifica
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 text-xs text-destructive"
                            onClick={() => elimina(c)}
                            data-testid={`costo-fisso-elimina-${c.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5 mr-1" /> Elimina
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })
            )}

            <p className="text-[11px] text-muted-foreground leading-relaxed">
              I costi fissi sono pensati per le uscite <strong>senza fattura</strong>: l'importo è
              quello realmente pagato, IVA inclusa. Se lo stesso costo arriva anche come fattura
              elettronica o uscita di cassa te lo segnalo come possibile doppione.
            </p>
          </>
        )}
      </main>

      <CostoFissoForm
        aperto={formAperto}
        onChiudi={() => { setFormAperto(false); setInModifica(null); }}
        costo={inModifica}
        opzioni={{
          categorie: dati?.categorie || [],
          cadenze: dati?.cadenze || [],
          metodi_pagamento: dati?.metodi_pagamento || [],
        }}
        onSalva={salva}
        salvando={salvando}
      />
    </div>
  );
};

export default AcquistiCostiFissiPage;
